import {
  Component,
  computed,
  DestroyRef,
  HostListener,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import type { ActivatedRouteSnapshot } from '@angular/router';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { AuthService } from './core/auth/auth.service';
import { ClientMeService } from './core/client/client-me.service';
import { LEGAL_DOCUMENTS } from './core/legal/documents';
import { signInParams } from './core/auth/return-url';
import { InstallPromptService } from './core/install/install-prompt.service';
import { CartService } from './core/cart/cart.service';
import { ProducerService } from './core/producer/producer.service';
import { ShopSkinService } from './core/shop/shop-skin.service';
import {
  CtAccountMenu,
  CtButton,
  CtCartBar,
  CtClientNav,
  CtInstallBanner,
  CtProducerNav,
} from './ui';

/** Segments `/producteur/<segment>` qui ouvrent le dock ops (pas une fiche ferme). */
const PRODUCER_OPS_SEGMENTS = new Set([
  'tableau-de-bord',
  'paliers',
  'boutique',
  'affiche',
  'points-de-retrait',
  'equipe',
  'produits',
  'commandes',
  'scan',
  'avis',
  'succes',
  'statistiques',
  'paiement',
]);

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    CtAccountMenu,
    CtButton,
    CtCartBar,
    CtClientNav,
    CtInstallBanner,
    CtProducerNav,
  ],
  styleUrl: './app.scss',
  templateUrl: './app.html',
})
export class App {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly producer = inject(ProducerService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly clientMe = inject(ClientMeService);
  protected readonly install = inject(InstallPromptService);
  protected readonly cart = inject(CartService);
  protected readonly shopSkin = inject(ShopSkinService);

  protected readonly isAuthenticated = this.auth.isAuthenticated;
  protected readonly profile = this.auth.profile;
  protected readonly legalDocuments = LEGAL_DOCUMENTS;

  /**
   * Chaque navigation terminée, plus une émission immédiate à l'abonnement pour
   * que les signaux dérivés aient une valeur dès le premier rendu.
   */
  private readonly navigation = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    startWith(null),
  );

  private readonly url = toSignal(this.navigation.pipe(map(() => this.router.url)), {
    requireSync: true,
  });

  /** Viewport étroit : dock client + header auto-hide (même seuil que le CSS). */
  protected readonly isNarrow = signal(
    typeof matchMedia === 'function' ? matchMedia('(width < 34rem)').matches : false,
  );

  /** Header mobile : masqué en scroll vers le bas, réaffiché en remontant. */
  protected readonly headerCollapsed = signal(false);
  private lastScrollY = 0;

  /**
   * Espace producteur opérationnel : pas d’en-tête marque, dock bas à portée
   * de pouce. Les fiches publiques `/producteur/:id` restent en chrome client.
   */
  protected readonly producerOps = computed(() => {
    if (!this.isAuthenticated()) {
      return false;
    }
    const path = (this.url().split('?')[0] ?? '').split('#')[0] ?? '';
    if (path === '/producteur') {
      return true;
    }
    if (!path.startsWith('/producteur/')) {
      return false;
    }
    const segment = path.slice('/producteur/'.length).split('/')[0] ?? '';
    return PRODUCER_OPS_SEGMENTS.has(segment);
  });

  /** Chrome client (hors ops producteur) : header + dock mobile. */
  protected readonly clientChrome = computed(() => !this.producerOps());

  /** Producteur déjà engagé : le lien « Vendre » du header n’a plus de sens. */
  protected readonly hideSellLink = computed(
    () => this.producerOps() || this.producer.hasProfile() === true,
  );

  /**
   * Le lien emporte la page courante : se connecter depuis l'en-tête ne doit pas
   * faire perdre au visiteur le produit qu'il était en train de regarder.
   */
  protected readonly signInParams = toSignal(
    this.navigation.pipe(map(() => signInParams(this.router.url))),
    { requireSync: true },
  );

  /**
   * Largeur de la colonne, décidée par la route et non par l'écran : la
   * découverte a besoin d'une grille, un formulaire de connexion non.
   */
  protected readonly wide = toSignal(
    this.navigation.pipe(map(() => isWideRoute(this.router.routerState.snapshot.root))),
    { requireSync: true },
  );

  constructor() {
    void this.producer.ensureLoaded();
    if (typeof matchMedia === 'function') {
      const mq = matchMedia('(width < 34rem)');
      const onChange = () => {
        this.isNarrow.set(mq.matches);
        if (!mq.matches) {
          this.headerCollapsed.set(false);
        }
      };
      mq.addEventListener('change', onChange);
      this.destroyRef.onDestroy(() => mq.removeEventListener('change', onChange));
    }

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.headerCollapsed.set(false);
        this.lastScrollY = typeof window !== 'undefined' ? window.scrollY : 0;
      });
  }

  @HostListener('window:scroll')
  protected onWindowScroll(): void {
    if (!this.clientChrome() || !this.isNarrow()) {
      return;
    }
    const y = window.scrollY;
    const delta = y - this.lastScrollY;
    if (y < 48) {
      this.headerCollapsed.set(false);
    } else if (delta > 8) {
      this.headerCollapsed.set(true);
    } else if (delta < -8) {
      this.headerCollapsed.set(false);
    }
    this.lastScrollY = y;
  }

  protected async signOut(): Promise<void> {
    await this.auth.signOut();
    // Le profil producteur est mémoïsé pour la session : sans cet oubli, le
    // compte suivant à se connecter sur le même navigateur verrait le statut
    // producteur du précédent.
    this.producer.clear();
    this.clientMe.clear();
    await this.router.navigateByUrl('/');
  }
}

/**
 * L'indication est portée par la route la plus profonde qui la déclare : elle
 * appartient à l'écran affiché, que la coquille n'a pas à connaître nommément.
 */
function isWideRoute(root: ActivatedRouteSnapshot): boolean {
  for (let route: ActivatedRouteSnapshot | null = root; route; route = route.firstChild) {
    if (route.data['wide'] === true) {
      return true;
    }
  }
  return false;
}
